<template>
  <div>
    <Loader v-if="isDataLoading" />
    <template v-else>
      <Table
        :title="$t('views.administration.addUserTitle')"
        :addLabel="$t('views.administration.addUser')"
        :headers="usersHeaders"
        :rows="usersRows"
        :values="users"
        @add="openModalAdd"
        @edit="openModalEdit"
        @delete="openModalDelete"
        @export="exportUsers"/>
    </template>

    <Loader v-if="isDataRoleLoading" />

    <template v-else>
      <Table
        :title="$t('views.administration.addRoleTitle')"
        :addLabel="$t('views.administration.addRole')"
        :headers="rolesHeaders"
        :rows="rolesRows"
        :values="roles"
        @add="openModalAddRole"
        @edit="openModalEditRole"
        @delete="openModalDeleteRole"
      />
    </template>

    <section class="section">
      <HeaderSection :title="$t('views.administration.mailing.title')"/>
      <div class="pa-30">
        <div class="row forms">
            <VqInput
              v-if="hasAgilityAccess"
              classes="col-tab-6"
              :label="$t('views.administration.mailing.form.monitoring')"
              name="monitoring"
              :tooltip="c('administration:monitoring_mail')"
              type="text"
              :errors="errors.email_monitoring"
              v-model="formData.email_monitoring"
            />
            <VqInput
              v-model="formData.email_maintenance"
              :tooltip="c('administration:maintenance_mail')"
              :errors="errors.email_maintenance"
              classes="col-tab-6"
              :label="$t('views.administration.mailing.form.maintenance')"
              name="maintenance"
              type="text"
            />
            <VqInput
              v-if="hasBackupAccess"
              v-model="formData.email_backup"
              :tooltip="c('administration:backup_mail')"
              :errors="errors.email_backup"
              classes="col-tab-6 mt-10"
              :label="$t('views.administration.mailing.form.backup')"
              name="backup"
              type="text"
            />
            <VqSelect
              v-if="hasBackupAccess"
              v-model="formData.email_backup_frequency"
              :options="frequencyOptions"
              class="mt-10 col-tab-6"
              name="backups_frequency"
              :label="$t('views.administration.mailing.form.backupFrequency')"
              option-label-attr="label"
              option-value-attr="value"
            />
          <div class="col-tab-6 is-right-aligned">
            <button
            v-if="$store.state.display_edit"
            :class="{'is-loading': isFormLoading}"
            type="button"
            class="button is-small is-gray-400 mt-30"
            @click="saveBroadcast">
            {{ $t('views.administration.mailing.form.cta') }}</button>
            {{ alert }}
          </div>
        </div>
      </div>
    </section>
    <ModalAddEditUser
      :roles="roles"
      @createuser="addUser"
      @edituser="editUser"
    />
    <ModalDeleteUser
      :current-user="user"
      @deleteuser="removeUser"
    />
    <ModalAddEditRole
      @createrole="addRole"
      @editrole="editRole"
    />
    <ModalDeleteRole
      @deleterole="removeRole"
    />

  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import USERS from '@/api/users'
import ROLE from '@/api/roles'

import BROADCAST from '@/api/broadcast'

import Loader from '@/components/Loader.vue'
import Table from '@/components/Table.vue'
import HeaderSection from '@/components/HeaderSection.vue'

import VqInput from '@/components/VqInput.vue'
import VqSelect from '@/components/VqSelect.vue'

import { eventHubMixin } from '@/mixins/event'

import ModalAddEditUser from '@/components/ModalAddEditUser.vue'
import ModalAddEditRole from '@/components/ModalAddEditRole.vue'

import ModalDeleteUser from '@/components/ModalDeleteUser.vue'
import ModalDeleteRole from '@/components/ModalDeleteRole.vue'


export default {
  name: 'administration',
  mixins: [eventHubMixin],
  components: {
    HeaderSection,
    Table,
    Loader,
    VqInput,
    VqSelect,
    ModalAddEditUser,
    ModalAddEditRole,
    ModalDeleteUser,
    ModalDeleteRole
  },
  data() {
    return {
      users: [],
      roles: [],
      isDataLoading: true,
      isDataRoleLoading: true,
      isFormLoading: false,
      formData: {
        monitoring: '',
        maintenance: '',
        backup: '',
        email_backup_frequency: ''
      },
      errors: {},
      alert: '',
      frequencyOptions: [{
        label: this.$t('views.administration.config.frequencyOptions.never'),
        value: 'never',
      },
      {
        label: this.$t('views.administration.config.frequencyOptions.daily'),
        value: 'daily',
      },
      {
        label: this.$t('views.administration.config.frequencyOptions.weekly'),
        value: 'weekly',
      },
      // {
      //   label: 'Monthly',
      //   value: 'monthly',
      // }
      ],
      radioOptions: [
        {
          label: this.$t('views.administration.config.radioOptions.yes'),
          value: true,
        },
        {
          label: this.$t('views.administration.config.radioOptions.no'),
          value: false,
        },
      ],
      usersHeaders: [
        {
          label: this.$t('views.administration.config.usersHeaders.givenName'),
          field: 'given_name',
        },
        {
          label: this.$t('views.administration.config.usersHeaders.sn'),
          field: 'sn',
        },
        {
          label: this.$t('views.administration.config.usersHeaders.mail'),
          field: 'mail',
        },
        {
          label: this.$t('views.administration.config.usersHeaders.phone'),
          field: 'phone',
        },
        {
          label: this.$t('views.administration.config.usersHeaders.username'),
          field: 'username',
        },
        {
          label: this.$t('views.administration.config.usersHeaders.status'),
          field: 'status',
        },
        {
          label: this.$t('views.administration.config.usersHeaders.roles'),
          field: 'roles_name',
        },
      ],
      rolesHeaders: [
        {
          label: this.$t('views.administration.config.rolesHeaders.name'),
          field: 'name'
        },
        {
          label: this.$t('views.administration.config.rolesHeaders.permissions'),
        },
      ],
      usersHeaderActions: [
        {
          title: '',
          classList: 'is-white is-square',
          icon: 'csv',
          action: 'export',
        },
        {
          title: 'Invite user',
          classList: 'is-gamma',
          icon: 'add-user',
          action: 'this.addUser()',
        },
      ],
      usersRows: [
        {
          value: row => row.given_name,
        },
        {
          value: row => row.sn,
        },
        {
          value: row => row.mail,
          link: row => `mailto:${row.mail}`,
          color: () => 'gray-700',
        },
        {
          value: row => row.phone,
        },
        {
          value: row => row.username,
        },
        {
          value: row => row.status === 'Enabled' ? this.$t('views.administration.enabled') : this.$t('views.administration.disabled'),
        },
        {
          value: row => row.is_customer_admin ? "Admin" : `${row.roles_name}`
        },
      ],

      rolesRows: [
        {
          value: row => row.name,
        },
        {
          value: row => this.getPermissionsVerbose(row)
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      hasAgilityAccess: 'auth/hasAgilityAccess',
      hasBackupAccess: 'auth/hasBackupAccess',
    }),
  },
  created() {
    this.init()
    this.initFormErrors()
    this.listBroadcast()
  },
  mounted() {
  },
  methods: {
    getPermissionsVerbose(row) {
      const copy = { ...row }
      delete copy.id;
      delete copy.name;
      delete copy.verbose_permissions;
      const verbose = []
      for (const [key, value] of Object.entries(copy)) {
        if (value) {
          verbose.push(this.$t(`roles.permissions.${key}`))
        }
      }
      return verbose.join(", ")
    },
    init() {
      this.listUsers()
      this.listRoles()
    },

    initFormErrors() {
      this.errors = {
        monitoring: [],
        maintenance: [],
        backup: [],
      }
    },

    listUsers() {
      USERS.list().then((res) => {
        this.isDataLoading = false
        if (res.data) {
          this.users = res.data
        }
      }, () => {
          this.isDataLoading = false
      })
    },

    listRoles() {
      ROLE.list().then((res) => {
        this.roles = res.data
        this.isDataRoleLoading = false

      })
    },

    listBroadcast() {
      BROADCAST.list().then(
        (res) => {
          if (res.data) {
            this.formData = { ...this.formData, ...res.data }
          }
        }, () => {
        },
      )
    },

    addUser(data) {
      data.status = 'Disabled'
      this.users.push(data)
    },

    removeUser(data) {
      this.users = this.users.filter(user => user.username !== data)
    },

    removeRole(id) {
      this.roles = this.roles.filter(role => role.id !== id)
    },

    editUser(data) {
      this.users = this.users.map((user => ((user.username === data.username) ? data : user)))
    },

    emitBroadcastToaster(response) {
      this.emitAddToaster({
        title: this.$t('views.administration.toasts.broadcast.title'),
        text: this.$t('views.administration.toasts.broadcast.content'),
        state: 'success',
      })
    },

    saveBroadcast() {
      this.isFormLoading = true
      BROADCAST.create(this.formData).then(
        (res) => {
          this.isFormLoading = false
          this.emitBroadcastToaster(res.data.response)
        },
        (err) => {
          this.isFormLoading = false
          Object.assign(this.errors, err.response.data)
        },
      )
    },

    addRole(data) {
      this.roles.push(data)
    },

    editRole(data) {
      this.roles = this.roles.map((role => (role.id === data.id) ? data : role))
    },

    openModalAddRole(data) {
      this.eventHub.$emit('modal:add-edit-role:open', data)
    },

    openModalEditRole(data) {
      this.eventHub.$emit('modal:add-edit-role:open', data)
    },

    openModalDeleteRole(data) {
      this.eventHub.$emit('modal:delete-role:open', data)
    },

    openModalAdd(data) {
      this.eventHub.$emit('modal:add-edit-user:open', data)
    },

    openModalEdit(data) {
      this.eventHub.$emit('modal:add-edit-user:open', data)
    },

    openModalDelete(data) {
      this.eventHub.$emit('modal:delete-user:open', data)
    },

    exportUsers() {
      USERS.export()
    },
  },
}
</script>
